import React from "react";
import CaseStudiesCard from "../CaseStudiesCard";
import Rule from "../../../Rule";
import Heading from "../../../Heading";
import Atomic from "../../../../img/casestudy/design-systems/atomic.png";
import Config from "../../../../img/casestudy/design-systems/config.png";
import Objective from "../../../../img/casestudy/design-systems/objective.png";
import Detach from "../../../../img/casestudy/design-systems/detach.png";
import Image from "../../../Image";
import Box from "../../../Box";
import Stack from "../../../Stack";
import Links from "../../../Links";
import BlogLayout from "../BlogLayout";

const Adoption = (
  <CaseStudiesCard id="adoption">
    <Heading
      variant="smaller"
      style={{ marginTop: 0, marginBottom: "var(--spacing-x-tiny)", textTransform: "uppercase" }}
    >
      Case Study
    </Heading>
    <p style={{ marginBottom: 0, fontSize: "var(--font-size-text-medium)" }}>
      Driving adoption of our design system over custom solutions
    </p>
    <Rule spacing="medium" inverted mt="var(--spacing-medium)" mb="var(--spacing-large)" />

    <BlogLayout>
      <Heading variant="larger">Feelix Adoption</Heading>

      <Stack spacing="var(--spacing-large)">
        <Box>
          <Heading variant="small">Overview</Heading>
          <p>
            MYOB undertook a replatform of its SME accounting software, and the Feelix Design System team played a
            critical role. Product Designers were expected to use over <strong>95% Feelix Components</strong> to ensure
            consistency of experiences while meeting business deadlines for a full product launch. This approach
            reinforced the value of established patterns, reduced custom UI creation, and enabled efficiency across the
            platform.
          </p>
        </Box>

        <Box>
          <Heading variant="small">Context</Heading>
          <p>
            Maintaining delivery velocity while improving design consistency was critical. The directive was clear:
            tighten experience quality, minimise customisation, and meet replatform milestones through a unified design
            system approach.
          </p>
        </Box>

        <Box>
          <Heading variant="small">Approach</Heading>
          <p style={{ marginBottom: "var(--spacing-x-medium)" }}>
            To promote a more systematic way of working, we held an all-hands session with the Product Design team. The
            goal was to align teams around system adoption, demonstrate practical techniques for creatively recombining
            components, and establish a shared mindset for building consistent, flexible experiences.
          </p>

          <Image dark src={Objective} alt="Objective & Impact" caption="Objective & Impact" />

          <p style={{ marginTop: "var(--spacing-x-medium)", marginBottom: "var(--spacing-x-medium)" }}>
            The deck introduced practical mindsets and analogies, from atomic design principles to Lego-style modular
            thinking, and demonstrated responsible component configuration.
          </p>

          <Image dark src={Atomic} alt="Atomic Design Thinking" caption="Atomic Design Thinking" />

          <p style={{ marginTop: "var(--spacing-x-medium)", marginBottom: "var(--spacing-x-medium)" }}>
            Designers learned how to creatively recombine existing components without detaching or rebuilding them.
            Hands-on exercises reinforced the principles and helped teams use system flexibility responsibly to achieve
            complex experiences without breaking component integrity.
          </p>

          <Image dark src={Detach} alt="Avoid Detaching Components" caption="Avoid Detaching Components" />
        </Box>

        <Box>
          <Heading variant="small">Outcome</Heading>
          <p>
            The deck set the tone for system adoption, framing it as a creative challenge rather than a limitation.
            Designers gained practical experience using Feelix components to build consistent, flexible interfaces,
            contributing to faster delivery and a more unified user experience across the replatform.
          </p>
          <p style={{ marginBottom: "var(--spacing-x-medium)" }}>
            Through this process, designers upskilled and deepened their understanding of how the system worked. They
            learned where flexibility existed, how to make component recommendations, and when to contribute new shared
            patterns.
          </p>

          <Image dark src={Config} alt="Flexibility & Configuration" caption="Flexibility & Configuration" />

          <p style={{ marginTop: "var(--spacing-x-medium)" }}>
            Feelix became the foundation for the rebuild of MYOB’s accounting software, offering a complete set of UI
            building blocks that replaced the static Bootstrap interface with a responsive design working seamlessly
            across mobile, tablet, and desktop. This shift was well received by customers and marked a major improvement
            in usability and consistency.
          </p>

          <p>
            Internally, adopting a mobile-first, device-agnostic architecture represented a significant evolution in
            front-end development. My work supported this vision through the creation of a single source-of-truth
            portal, uplifting Feelix components, and broader guidelines that enabled the system to scale across teams.
          </p>
          <p>
            This work directly supported the successful release of the MYOB replatform and demonstrates how Feelix
            enabled it. You can read more in the case study on{" "}
            <Links href="#maturefeelix" rel="noopener noreferrer">
              Maturing Feelix
            </Links>
            .
          </p>
        </Box>
      </Stack>
    </BlogLayout>
  </CaseStudiesCard>
);

export default Adoption;
